import { useState, useEffect, useCallback } from 'react';
import { Dropdown, Menu, Input, Modal, Radio, notification } from 'antd';
import { PictureOutlined, ShoppingCartOutlined, PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import { cardsAPI, projectsAPI, isAuthenticated, type MinimalProject } from '../../service/django-api';
import { useCard } from '../../service/use-card';

function getCanvasBlob(): Promise<Blob | null> {
  const canvas = document.getElementById('export-canvas') as HTMLCanvasElement | null;
  if (!canvas) return Promise.resolve(null);
  return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
}

function openLoginModal() {
  const detail = { mode: 'login' };
  window.dispatchEvent(new CustomEvent('openAuthModal', { detail }));
  try {
    if (window.parent && window.parent !== window) {
      window.parent.dispatchEvent(new CustomEvent('openAuthModal', { detail }));
    }
  } catch {}
}

/** Styled to match the .action-tab-button class in the Yugi sidebar */
export function GalleryCartActions() {
  const { card: currentCard } = useCard();
  const [projects, setProjects] = useState<MinimalProject[]>([]);
  const [saving, setSaving] = useState(false);
  const [galleryConfirmOpen, setGalleryConfirmOpen] = useState(false);
  const [galleryVisibility, setGalleryVisibility] = useState<'private' | 'public'>('private');
  const [orderLoading, setOrderLoading] = useState(false);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');

  const fetchProjects = useCallback(async () => {
    if (!isAuthenticated()) return;
    try { setProjects(await projectsAPI.getMinimalList()) } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchProjects() }, [fetchProjects]);

  const buildPayload = useCallback(async () => {
    const { name, artData } = currentCard as any;
    if (!name || name === 'Untitled Card') throw new Error('Please enter a card title before uploading.');
    if (!artData) throw new Error('Please add artwork before uploading.');
    const blob = await getCanvasBlob();
    if (!blob) throw new Error('Could not capture card image.');
    const title = name;
    const cardType = (currentCard as any).frameType === 'pendulum' ? 'PENDULUM' : ((currentCard as any).format || 'tcg').toUpperCase();
    const rawCard: any = { ...currentCard };
    delete rawCard.artData;
    delete rawCard.backgroundData;
    const cardConfig = { schemaVersion: '1.0.0', cardType: 'yugioh', ...rawCard };
    const file = new File([blob], 'card.png', { type: 'image/png' });
    return { title, cardType, cardConfig, file };
  }, [currentCard]);

  // ── Gallery ────────────────────────────────────────────────
  const handleGallery = async () => {
    if (!isAuthenticated()) {
      openLoginModal();
      return;
    }
    const { name, artData } = currentCard as any;
    if (!name || name === 'Untitled Card') {
      notification.error({ message: 'Upload failed', description: 'Please enter a card title before uploading.', duration: 3 });
      return;
    }
    if (!artData) {
      notification.error({ message: 'Upload failed', description: 'Please add artwork before uploading.', duration: 3 });
      return;
    }
    setGalleryConfirmOpen(true);
  };

  const handleGalleryConfirm = async () => {
    setSaving(true);
    try {
      const { title, cardType, cardConfig, file } = await buildPayload();
      await cardsAPI.create({
        title,
        cardType,
        frameVersion: '1.0.0',
        cardConfig,
        highResImage: file,
        isPublic: galleryVisibility === 'public',
      });
      setGalleryConfirmOpen(false);
      notification.success({ message: 'Uploaded to Gallery!', description: `"${title}" saved.`, duration: 3 });
      window.parent?.postMessage({ type: 'gamification-updated' }, '*');
    } catch (e: any) {
      notification.error({ message: 'Upload failed', description: e?.message, duration: 3 });
    } finally { setSaving(false) }
  };

  // ── Add to Cart ────────────────────────────────────────────
  const addToProject = async (projectId: string) => {
    setOrderLoading(true);
    try {
      const { title, cardType, cardConfig, file } = await buildPayload();
      await projectsAPI.uploadAndAddToProject({ title, cardType, frameVersion: '1.0.0', cardConfig, highResImage: file, projectId, quantity: 1, face: 'FRONT' });
      const project = projects.find(p => p.id === projectId);
      notification.success({ message: 'Added to Cart!', description: `"${title}" → ${project?.name || 'project'}.`, duration: 3 });
      window.parent?.postMessage({ type: 'cart-updated' }, '*');
    } catch (e: any) {
      notification.error({ message: 'Add to Cart failed', description: e?.message, duration: 3 });
    } finally { setOrderLoading(false) }
  };

  const handleCreateAndAdd = async () => {
    if (!isAuthenticated()) {
      openLoginModal();
      return;
    }
    if (!newProjectName.trim()) return;
    setOrderLoading(true);
    try {
      const project = await projectsAPI.create({ name: newProjectName.trim(), language: 'en' });
      setShowNewProject(false);
      setNewProjectName('');
      setProjects(await projectsAPI.getMinimalList());
      await addToProject(project.id);
    } catch (e: any) {
      notification.error({ message: 'Failed', description: e?.message, duration: 3 });
      setOrderLoading(false);
    }
  };

  const authenticated = isAuthenticated();
  const handleCart = () => {
    if (!authenticated) {
      openLoginModal();
      return;
    }
    setShowNewProject(true);
  };

  const projectMenu = (
    <Menu>
      {projects.map(p => (
        <Menu.Item key={p.id} onClick={() => addToProject(p.id)}>
          <span style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
            {p.name}
            <span style={{ color: 'var(--brand-gold)', fontSize: 11 }}>{p.card_count}</span>
          </span>
        </Menu.Item>
      ))}
    </Menu>
  );

  if (showNewProject) {
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input
          size="small"
          placeholder="Project name…"
          value={newProjectName}
          onChange={e => setNewProjectName(e.target.value)}
          onPressEnter={handleCreateAndAdd}
          autoFocus
          style={{ flex: '1 1 120px', minWidth: 100 }}
        />
        <button className="action-tab-button" onClick={handleCreateAndAdd} disabled={!newProjectName.trim() || orderLoading}>
          {orderLoading ? <LoadingOutlined /> : <PlusOutlined />}
          <span>CREATE</span>
        </button>
        <button className="action-tab-button" onClick={() => { setShowNewProject(false); setNewProjectName('') }}>
          <span>CANCEL</span>
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Gallery */}
      <button className="action-tab-button" onClick={handleGallery} disabled={saving}>
        {saving ? <LoadingOutlined /> : <PictureOutlined />}
        <span>UPLOAD TO GALLERY</span>
      </button>

      {/* Order — dropdown if projects exist, create-new if none */}
      <style>{`
        /* Doubled selector for specificity to beat global tailwind.css !important rules */
        .gallery-upload-modal.gallery-upload-modal .ant-modal-content {
          background: #0d0d0c !important;
          border: 1px solid rgba(212, 178, 84, 0.25) !important;
          border-radius: 20px !important;
          box-shadow: 0 24px 70px rgba(0, 0, 0, 0.85), inset 0 1px 0 rgba(255, 255, 255, 0.04) !important;
          overflow: hidden !important;
        }
        .gallery-upload-modal.gallery-upload-modal .ant-modal-header {
          background: transparent !important;
          border-bottom: none !important;
          padding: 32px 32px 16px !important;
        }
        .gallery-upload-modal.gallery-upload-modal .ant-modal-title {
          color: #d4b254 !important;
          font-size: 22px !important;
          font-weight: 800 !important;
          letter-spacing: 0.05em !important;
          text-transform: uppercase !important;
        }
        .gallery-upload-modal.gallery-upload-modal .ant-modal-body {
          padding: 0 32px 32px !important;
          background: #0d0d0c !important;
          color: rgba(255, 255, 255, 0.75) !important;
        }
        .gallery-upload-modal.gallery-upload-modal .ant-modal-close {
          top: 30px !important;
          right: 32px !important;
          width: 34px !important;
          height: 34px !important;
          border-radius: 50% !important;
          border: 1px solid rgba(212, 178, 84, 0.25) !important;
          background: rgba(0, 0, 0, 0.3) !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          transition: all 0.2s ease !important;
        }
        .gallery-upload-modal.gallery-upload-modal .ant-modal-close:hover {
          border-color: #d4b254 !important;
          background: rgba(212, 178, 84, 0.1) !important;
        }
        .gallery-upload-modal.gallery-upload-modal .ant-modal-close-x {
          width: 34px !important;
          height: 34px !important;
          line-height: 34px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          color: #fff !important;
        }
        .gallery-upload-modal.gallery-upload-modal .ant-radio-button-wrapper {
          background: transparent !important;
          border: 1px solid rgba(212, 178, 84, 0.24) !important;
          color: #fff !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          font-size: 15px !important;
          height: 48px !important;
          line-height: 46px !important;
          text-align: center !important;
          border-radius: 999px !important;
          transition: all 0.2s ease !important;
          padding: 0 !important;
          flex: unset !important;
          min-width: unset !important;
          min-height: unset !important;
          display: block !important;
          border-left-width: 1px !important;
        }
        .gallery-upload-modal.gallery-upload-modal .ant-radio-button-wrapper::before {
          display: none !important;
        }
        .gallery-upload-modal.gallery-upload-modal .ant-radio-button-wrapper:hover:not(.ant-radio-button-wrapper-disabled) {
          color: #fff !important;
          border-color: #d4b254 !important;
          background: rgba(212, 178, 84, 0.05) !important;
        }
        .gallery-upload-modal.gallery-upload-modal .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
          background: #d4b254 !important;
          border-color: #d4b254 !important;
          color: #0c0c0b !important;
          font-weight: 700 !important;
          box-shadow: 0 4px 16px rgba(212, 178, 84, 0.25) !important;
        }
        .gallery-upload-modal.gallery-upload-modal .ant-radio-button-wrapper-disabled {
          opacity: 0.5 !important;
          cursor: not-allowed !important;
        }
        .gallery-upload-modal.gallery-upload-modal .ant-radio-group {
          display: grid !important;
          width: 100% !important;
          gap: 16px !important;
          flex-wrap: unset !important;
        }
        .gallery-upload-modal.gallery-upload-modal a {
          color: #d4b254 !important;
          font-weight: 600 !important;
          transition: color 0.2s ease !important;
          text-decoration: none !important;
        }
        .gallery-upload-modal.gallery-upload-modal a:hover {
          color: #e5c767 !important;
          text-decoration: underline !important;
        }
        .gallery-upload-modal.gallery-upload-modal .modal-cancel-btn {
          min-width: 120px !important;
          height: 44px !important;
          border-radius: 999px !important;
          border: 1px solid rgba(212, 178, 84, 0.24) !important;
          background: transparent !important;
          color: #fff !important;
          font-weight: 700 !important;
          font-size: 14px !important;
          letter-spacing: 0.05em !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
        }
        .gallery-upload-modal.gallery-upload-modal .modal-cancel-btn:hover:not(:disabled) {
          border-color: #d4b254 !important;
          background: rgba(212, 178, 84, 0.05) !important;
        }
        .gallery-upload-modal.gallery-upload-modal .modal-cancel-btn:disabled {
          opacity: 0.5 !important;
          cursor: not-allowed !important;
        }
        .gallery-upload-modal.gallery-upload-modal .modal-confirm-btn {
          min-width: 140px !important;
          height: 44px !important;
          border-radius: 999px !important;
          border: 1px solid #d4b254 !important;
          background: #d4b254 !important;
          color: #0c0c0b !important;
          font-weight: 700 !important;
          font-size: 14px !important;
          letter-spacing: 0.05em !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 8px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          box-shadow: 0 4px 16px rgba(212, 178, 84, 0.25) !important;
        }
        .gallery-upload-modal.gallery-upload-modal .modal-confirm-btn:hover:not(:disabled) {
          background: #e5c767 !important;
          border-color: #e5c767 !important;
          box-shadow: 0 6px 20px rgba(212, 178, 84, 0.35) !important;
        }
        .gallery-upload-modal.gallery-upload-modal .modal-confirm-btn:disabled {
          opacity: 0.6 !important;
          cursor: not-allowed !important;
          box-shadow: none !important;
        }
      `}</style>

      <Modal
        className="gallery-upload-modal"
        title="Upload to Gallery"
        visible={galleryConfirmOpen}
        onCancel={() => !saving && setGalleryConfirmOpen(false)}
        footer={null}
        destroyOnClose
        centered
        width={520}
        maskStyle={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0, 0, 0, 0.65)' }}
        closeIcon={<span style={{ fontSize: 13, fontFamily: 'sans-serif', fontWeight: 700, color: '#fff', opacity: 0.9 }}>X</span>}
      >
        <p style={{ color: 'rgba(255, 255, 255, 0.75)', fontSize: 15, lineHeight: 1.6, margin: 0 }}>
          You are about to upload <strong>"{(currentCard as any).name}"</strong> to your gallery. By uploading, you agree to our{' '}
          <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">Terms of Service</a>
          {' '}and{' '}
          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
        </p>
        <Radio.Group
          value={galleryVisibility}
          onChange={e => setGalleryVisibility(e.target.value)}
          disabled={saving}
          optionType="button"
          buttonStyle="solid"
          style={{ gridTemplateColumns: '1fr 1fr', margin: '24px 0 32px' }}
        >
          <Radio.Button value="private">PRIVATE</Radio.Button>
          <Radio.Button value="public">PUBLIC</Radio.Button>
        </Radio.Group>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <button
            onClick={() => setGalleryConfirmOpen(false)}
            disabled={saving}
            className="modal-cancel-btn"
          >
            CANCEL
          </button>
          <button
            onClick={handleGalleryConfirm}
            disabled={saving}
            className="modal-confirm-btn"
          >
            {saving ? <LoadingOutlined /> : <PictureOutlined />}
            {saving ? 'UPLOADING...' : 'UPLOAD'}
          </button>
        </div>
      </Modal>
      
      {projects.length > 0 ? (
        <Dropdown overlay={projectMenu} trigger={['click']} disabled={orderLoading}>
          <button className="action-tab-button" disabled={orderLoading}>
            {orderLoading ? <LoadingOutlined /> : <ShoppingCartOutlined />}
            <span>CART</span>
          </button>
        </Dropdown>
      ) : (
        <button className="action-tab-button" onClick={handleCart} disabled={orderLoading}>
          {orderLoading ? <LoadingOutlined /> : <ShoppingCartOutlined />}
          <span>CART</span>
        </button>
      )}
    </>
  );
}
